<template>
  <bm-dropdown
    :items="enumValues"
    :item-text="(o) => $t(o.translationKey)"
    item-value="key"
    v-bind="$attrs"
    :value="keyedValues"
    :searchable="false"
    :sort-fn="sortFn"
    @update:value="ddlValueChanged"
    @focus="emit('focus')"
    @blur="emit('blur')"
  >
    <!-- Passes all given slots to BmDropdown -->
    <template v-for="(_, slot) of $slots" #[slot]>
      <slot :name="slot" />
    </template>
  </bm-dropdown>
</template>

<script lang="ts">
export default {
  name: "BmEnumDdl",
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import type BaseEnumType from "@/app/services/baseEnumType";
import type { Comparer } from "@/components/controls/grid/BmGridData";
import userStore from "@/store/modules/userStore";
import { translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";
import { computed } from "vue";
import type { Maybe } from "@/components/utils/typeHelpers";
import { isNullish } from "@/app/apiutils";

interface EnumDropdownType {
  getValues(): readonly BaseEnumType[];
}

interface BmEnumDdlProps {
  value?: Maybe<BaseEnumType[] | BaseEnumType>;
  enumType: EnumDropdownType;
  enumFilter?: (a: BaseEnumType) => boolean;
  /* The value to emit when the control is cleared/empty */
  clearedValue?: BaseEnumType | null;
  hideZero?: boolean;
  sortedInitialValue?: BaseEnumType;
}

interface BmEnumDdlEvents {
  (event: "update:value", value: BaseEnumType | BaseEnumType[] | null): void;
  (event: "focus"): void;
  (event: "blur"): void;
}

const props = withDefaults(defineProps<BmEnumDdlProps>(), {
  clearedValue: null,
  hideZero: false,
});

const emit = defineEmits<BmEnumDdlEvents>();

const keyedValues = computed((): number[] | number | null => {
  return isNullish(props.value)
    ? null
    : props.value instanceof Array
      ? props.value.map((v) => v.key) // Map values to their keys
      : props.value.key;
});

const enumValues = computed((): BaseEnumType[] =>
  props.enumType
    .getValues()
    .filter(
      (value) =>
        (!props.enumFilter || props.enumFilter(value)) &&
        (!props.hideZero || value.key !== 0)
    )
);

const enumValueForKey = (key: number): BaseEnumType | null =>
  enumValues.value.find((value) => value.key === key) ?? null;

const sortFn = computed((): Comparer | undefined => {
  return props.sortedInitialValue
    ? comparerWithDefinedInitialValue(props.sortedInitialValue)
    : undefined;
});

const ddlValueChanged = (val: Array<number> | number | undefined | null) => {
  if (val !== undefined && val !== null) {
    if (val instanceof Array) {
      emit(
        "update:value",
        val
          .map((key) => enumValueForKey(key))
          .filter((value): value is BaseEnumType => value !== null)
      );
    } else {
      emit("update:value", enumValueForKey(val));
    }
  } else {
    emit("update:value", props.clearedValue);
  }
};

/**
 * Alphabetically compares enum translations, but ensures
 * that the specified value will remain first in the
 * dropdown
 */
const comparerWithDefinedInitialValue = <T extends BaseEnumType>(
  initialValue: T
): Comparer => {
  return (a: T, b: T) => {
    if (a === initialValue) {
      return -1;
    }

    if (b === initialValue) {
      return 1;
    }

    const translationA = translate(Key.fromString(a.translationKey!));
    const translationB = translate(Key.fromString(b.translationKey!));

    return translationA.localeCompare(
      translationB,
      userStore.state.userLanguageLocale,
      {
        sensitivity: "base",
      }
    ) as 0 | 1 | -1;
  };
};
</script>
